import { useEffect, useMemo } from 'react';
import { useGetBusinessInsightsQuery } from 'store/api/apiSlice';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import {
  BusinessInsightsRequest,
  BusinessInfoResponse,
} from 'store/api/apiTypes';

/**
 * useBusinessInsights
 * Fetches business insights (v1) for currently selected project.
 */
export const useBusinessInsights = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );

  const activeReport = useAppSelector(state => state.history.activeReport);

  const queryParams = useMemo<BusinessInsightsRequest | null>(() => {
    if (!selectedProject?.domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return {
      domain: selectedProject.domain,
      reportId: activeReport?.id,
    };
  }, [selectedProject, activeReport]);

  const {
    data: insightsData,
    isLoading: insightsLoading,
    error: insightsError,
    refetch: refetchInsights,
    isFetching: insightsFetching,
  } = useGetBusinessInsightsQuery(queryParams as BusinessInsightsRequest, {
    skip: !queryParams,
    refetchOnMountOrArgChange: true,
  });

  const {
    showErrorModal: showInsightsErrorModal,
    handleApiError: handleInsightsApiError,
    handleRetry: handleInsightsRetry,
    hideErrorModal: hideInsightsErrorModal,
  } = useApiError(() => refetchInsights());

  useEffect(() => {
    if (insightsError) {
      handleInsightsApiError(insightsError);
    }
  }, [insightsError, handleInsightsApiError]);

  usePollingOnError(queryParams, insightsError, refetchInsights, insightsData);

  return {
    insightsData: insightsData as BusinessInfoResponse | undefined,
    businessInsights: insightsData as BusinessInfoResponse | undefined,
    insightsLoading,
    insightsError,
    refetchInsights,
    showInsightsErrorModal,
    handleInsightsRetry,
    hideInsightsErrorModal,
    hasInsightsData: !!insightsData,
    insightsProfile: insightsData?.business_profile,
    insightsCompanyName: insightsData?.business_profile?.business_name,
    insightsServices: insightsData?.services || [],
    insightsServicesCount: insightsData?.services?.length || 0,
    insightsFetching,
  };
};
